import React, { useEffect, useRef, useState } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { EqualHeight } from "react-equal-height/clean";
import FlipCard from "./FlipCard/FlipCard";
import { useIsMobile } from "./useIsMobile";
import { FlipCardsContainerProps } from "../flip-cards.props";

// Card column widths — must stay in sync with FlipCard wrapper width/minWidth.
const CARD_WIDTH_SM = "223px";
const CARD_WIDTH_MD = "373px";

const styles = {
  outerContainerStyles: {
    m: {
      xs: "0 20px",
      sm: "0 auto",
      md: "0 auto"
    },
    maxWidth: {
      sm: "693px",
      md: "1185px"
    },
    // At 600–692px the 3×223px grid (693px) overflows the viewport.
    // overflowX:auto shows a scrollbar/swipe on that narrow band instead of
    // hard-clipping edge cards and their box-shadows.
    overflowX: "auto",
    // Per the CSS overflow spec, an axis set to `auto` promotes the other
    // (otherwise `visible`) axis to `auto` too. Left implicit, that gives this
    // container an unwanted vertical scroll: when a bottom-row card flips, the
    // rotateY(180deg)+perspective transform bulges its rendered box past the
    // container's bottom edge, and the promoted overflow-y:auto surfaces a
    // secondary scrollbar. Pinning overflowY:hidden opts out of the promotion
    // and clips only the transient transform bleed — container height always
    // equals content height, so no real card content or legal copy is clipped.
    overflowY: "hidden"
  },
  cardsContainerStyles: {
    // CSS Grid equalizes row heights natively — no JS measurement needed.
    // Each row independently sizes to its tallest card, which also fixes the
    // stale-EH-height overlap that occurred on viewport resize.
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: `repeat(3, ${CARD_WIDTH_SM})`,
      md: `repeat(3, ${CARD_WIDTH_MD})`
    },
    justifyContent: "center",
    gap: {
      xs: "16px",
      sm: "12px",
      md: "24px 20px"
    },
    pt: "16px",
    pb: "32px"
  }
};

const FlipCards = (props: FlipCardsContainerProps) => {
  const { cards } = props;
  // Single breakpoint subscription for the whole card grid — threaded down as a
  // prop so memoized cards only re-render when the breakpoint actually crosses.
  const isMobile = useIsMobile();

  // EqualHeight manages the inner alignment groups (sub-heading, pricing, legal) so that those
  // rows line up across all cards. Back-face preview images use height:auto with no intrinsic size,
  // so the on-mount EH measurement can be too short if they haven't loaded yet. `reflowKey` is
  // bumped after all images settle, triggering EqualHeight to re-measure the inner groups.
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [reflowKey, setReflowKey] = useState(0);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) {
      return undefined;
    }
    let cancelled = false;
    let frame = 0;
    const bump = () => {
      if (cancelled) {
        return;
      }
      // Coalesce multiple loads in the same tick and let layout settle before re-measuring.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setReflowKey(prev => prev + 1));
    };

    const pending = Array.from(node.querySelectorAll("img")).filter(img => !img.complete);
    if (pending.length === 0) {
      // Images already cached — still trigger one post-paint re-measure in case the on-mount
      // measurement raced layout.
      bump();
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
      };
    }

    let remaining = pending.length;
    const onSettled = () => {
      remaining -= 1;
      if (remaining <= 0) {
        bump();
      }
    };
    pending.forEach(img => {
      img.addEventListener("load", onSettled, { once: true });
      img.addEventListener("error", onSettled, { once: true });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      pending.forEach(img => {
        img.removeEventListener("load", onSettled);
        img.removeEventListener("error", onSettled);
      });
    };
  }, []);

  if (!Array.isArray(cards) || cards.length === 0) {
    return null;
  }

  return (
    <DtvBox sx={styles.outerContainerStyles}>
      <EqualHeight updateOnChange={reflowKey}>
        <DtvBox ref={gridRef} sx={styles.cardsContainerStyles}>
          {cards.map((card, index) => {
            if (!card) {
 return null;
}
            return (
              <FlipCard
                card={card}
                isMobile={isMobile}
                // Cards come from CMS and never reorder client-side; queryparam
                // is not guaranteed unique, so positional keys are the safe choice.
                key={index}
              />
            );
          })}
        </DtvBox>
      </EqualHeight>
    </DtvBox>
  );
};

export default React.memo(FlipCards);
